import { Button } from "@aitisal/ui/retail-storefront/button";
import { useNavigate } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  SlidersHorizontal,
} from "lucide-react";
import { ReactNode, useState } from "react";

import RouteBreadcrumb from "../components/retail/RouteBreadcrumb";
import ProductCard from "../components/retail/ProductCard";
import { ProductListingSkeleton } from "../components/retail/RetailSkeletons";
import {
  PRICE_RANGES,
  useProductListing,
} from "../hooks/useProductListing";

// --- REUSABLE CONTAINER ---
function Container({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
  );
}

// Filter Section Component
const FilterSection = ({
  categories,
  selectedFilters,
  onFilterChange,
}: {
  categories: string[];
  selectedFilters: any;
  onFilterChange: (type: string, value: string, checked: boolean) => void;
}) => {
  const [openSections, setOpenSections] = useState({
    category: true,
    price: false,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const clearAllFilters = () => {
    onFilterChange("all", "", false);
  };

  const priceRanges = PRICE_RANGES;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-24">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5" />
          Filters
        </h3>
        <Button
          variant="link"
          onClick={clearAllFilters}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium h-auto p-0"
        >
          Clear All
        </Button>
      </div>

      {/* Category Filter */}
      <div className="mb-6 border-b border-gray-200 pb-4">
        <Button
          variant="ghost"
          onClick={() => toggleSection("category")}
          className="flex items-center justify-between w-full mb-3 p-0 h-auto font-normal hover:bg-transparent"
        >
          <h4 className="font-semibold text-gray-900">Category</h4>
          {openSections.category ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
        {openSections.category && (
          <div className="space-y-2">
            {categories.map((cat) => (
              <label
                key={cat}
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
              >
                <input
                  type="checkbox"
                  checked={selectedFilters.categories.includes(cat)}
                  onChange={(e) =>
                    onFilterChange("category", cat, e.target.checked)
                  }
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">{cat}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Price Range Filter */}
      <div className="mb-4">
        <Button
          variant="ghost"
          onClick={() => toggleSection("price")}
          className="flex items-center justify-between w-full mb-3 p-0 h-auto font-normal hover:bg-transparent"
        >
          <h4 className="font-semibold text-gray-900">Price Range</h4>
          {openSections.price ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
        {openSections.price && (
          <div className="space-y-2">
            {priceRanges.map((price) => (
              <label
                key={price}
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
              >
                <input
                  type="checkbox"
                  checked={selectedFilters.priceRanges.includes(price)}
                  onChange={(e) =>
                    onFilterChange("priceRange", price, e.target.checked)
                  }
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">{price}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Page Component — presentation-only over useProductListing
const ProductListingPage = ({
  preselectedCategory,
  searchQuery,
}: {
  preselectedCategory?: string;
  searchQuery?: string;
}) => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);

  const {
    isLoading,
    error,
    sortBy,
    setSortBy,
    currentPage,
    itemsPerPage,
    selectedFilters,
    handleFilterChange,
    clearFilters,
    categories,
    validItems,
    currentDisplayItems,
    totalPages,
    startIndex,
    handlePageChange,
  } = useProductListing({ preselectedCategory, searchQuery });

  const breadcrumbItems = [
    { label: "Home", path: "/c/retail" },
    { label: "Shop", path: "/c/shop" },
    {
      label: searchQuery
        ? `Search: "${searchQuery}"`
        : preselectedCategory || "All Products",
    },
  ];

  if (isLoading) {
    return <ProductListingSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Error Loading Products
          </h2>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Container>
        <div className="py-6">
          <div className="mb-4">
            <RouteBreadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              {searchQuery
                ? `Search Results for "${searchQuery}"`
                : preselectedCategory || "Browse Products"}
            </h1>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center justify-center gap-2 bg-white border-gray-300 rounded-lg px-4 py-3 mb-2 hover:bg-gray-50 h-auto"
            >
              <SlidersHorizontal className="w-5 h-5" />
              <span className="font-semibold">Filters</span>
            </Button>

            <aside
              className={`${showFilters ? "block" : "hidden"} lg:block lg:w-64 flex-shrink-0`}
            >
              <FilterSection
                categories={categories}
                selectedFilters={selectedFilters}
                onFilterChange={handleFilterChange}
              />
            </aside>

            <main className="flex-1 min-w-0">
              <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-sm sm:text-base text-gray-600">
                  Showing{" "}
                  <span className="font-bold text-gray-900">
                    {validItems.length > 0 ? startIndex + 1 : 0}-
                    {Math.min(startIndex + itemsPerPage, validItems.length)}
                  </span>{" "}
                  of{" "}
                  <span className="font-bold text-gray-900">
                    {validItems.length}
                  </span>{" "}
                  Items
                </p>
                <div className="relative w-full sm:w-auto min-w-[140px]">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full pl-3 pr-8 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring cursor-pointer appearance-none [-webkit-appearance:none] [-moz-appearance:none]"
                  >
                    <option value="featured">Sort by: Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name: A to Z</option>
                  </select>
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </div>
                </div>
              </div>

              {validItems.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-lg border border-dashed border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No products match your search
                  </h3>
                  <Button
                    onClick={() => {
                      clearFilters();
                      navigate({
                        to: "/c/shop",
                        search: { category: undefined, search: undefined },
                      });
                    }}
                    variant="outline"
                  >
                    Clear Search
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-6 pb-12">
                    {currentDisplayItems.map((item) => (
                      <div
                        key={item.itemId}
                        className="flex flex-col h-full relative"
                      >
                        <ProductCard item={item} />
                      </div>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8 pb-12">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() =>
                          handlePageChange(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                        className="rounded-full w-10 h-10 border-gray-300 disabled:opacity-50"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>

                      <div className="flex gap-1 py-10">
                        {Array.from(
                          { length: totalPages },
                          (_, i) => i + 1,
                        ).map(
                          (page) =>
                            (page === 1 ||
                              page === totalPages ||
                              (page >= currentPage - 1 &&
                                page <= currentPage + 1)) && (
                              <Button
                                key={page}
                                variant="ghost"
                                onClick={() => handlePageChange(page)}
                                className={`w-10 h-10 rounded-full text-sm font-medium p-0
                                ${
                                  currentPage === page
                                    ? "bg-[var(--brand-color)] text-white hover:bg-[var(--brand-color)] hover:text-white"
                                    : "text-gray-700 hover:bg-gray-100"
                                }`}
                              >
                                {page}
                              </Button>
                            ),
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() =>
                          handlePageChange(
                            Math.min(totalPages, currentPage + 1),
                          )
                        }
                        disabled={currentPage === totalPages}
                        className="rounded-full w-10 h-10 border-gray-300 disabled:opacity-50"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </main>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ProductListingPage;
